import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { LanguageProvider } from './contexts/LanguageContext';
import useAnalytics from './hooks/useAnalytics';

import Home from './components/Home';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import Dashboard from './components/dashboard/Dashboard';
import ActivityPage from './components/ActivityPage';
import InnovacionesPage from './components/InnovacionesPage';
import QueEsPrinconserPage from './components/QueEsPrinconserPage';
import FilosofiaPrinconserPage from './components/FilosofiaPrinconserPage';
import CurriculumHolisticoPage from './components/CurriculumHolisticoPage';
import SaludMentalPage from './components/SaludMentalPage';
import SaludSocialPage from './components/SaludSocialPage';
import SaludFisicaPage from './components/SaludFisicaPage';
import SaludFinancieraPage from './components/SaludFinancieraPage';
import MetodosEnsenanzaPage from './components/MetodosEnsenanzaPage';
import ConsejoSabiosPage from './components/ConsejoSabiosPage';
import GamificationPage from './components/GamificationPage';
import EcosistemaIncentivoPage from './components/EcosistemaIncentivoPage';
// Páginas de libros
import Libro1Page from './components/libros/Libro1Page';
import Libro2Page from './components/libros/Libro2Page';
import Libro3Page from './components/libros/Libro3Page';
import Libro4Page from './components/libros/Libro4Page';
import Libro5Page from './components/libros/Libro5Page';
import Libro6Page from './components/libros/Libro6Page';
import Libro7Page from './components/libros/Libro7Page';
import Libro8Page from './components/libros/Libro8Page';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUsersList from './components/admin/AdminUsersList';
import AdminUserDetail from './components/admin/AdminUserDetail';
import AdminActivitiesList from './components/admin/AdminActivitiesList';
import CodeGeneratorPanel from './components/admin/CodeGeneratorPanel';
import FinancialSystemPage from './components/admin/FinancialSystemPage';
import TfiatDistributionPage from './components/admin/TfiatDistributionPage';
import Treasury from './components/admin/Treasury';
import ExplorerForm from './components/ExplorerForm';
import TerminosCondiciones from './components/TerminosCondiciones';
import CompensationSystem from './components/CompensationSystem';
import SupportMaterials from './components/SupportMaterials';
import LandingPage from './components/LandingPage';
import LandingPageEN from './components/LandingPageEN';
import ExperimentosPage from './components/ExperimentosPage';
import InvestorRelations from './components/InvestorRelations';
import BlogPrinconser from './components/BlogPrinconser';
import ReservarPage from './components/ReservarPage';
import ReservarPageEN from './components/ReservarPageEN';
import ReservarFormPage from './components/ReservarFormPage';
import PoliticaPrivacidad from './components/PoliticaPrivacidad';
import Chatbot from './components/Chatbot';

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#7C3AED', // Morado
        },
        secondary: {
            main: '#22C55E', // Verde
        },
        background: {
            default: '#F3F4F6', // Fondo gris claro
        },
    },
    typography: {
        fontFamily: 'Montserrat, Roboto, Arial, sans-serif',
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                },
            },
        },
    },
});

// Componente para proteger rutas admin
const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const userStr = localStorage.getItem('authUser');
    const user = userStr ? JSON.parse(userStr) : null;
    if (!user || !user.is_admin) {
        return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
};

// Componente principal con analytics
const AppContent: React.FC = () => {
    // Inicializar analytics para tracking de páginas
    useAnalytics();
    
    return (
        <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/login" element={<LoginForm />} />
                                <Route path="/register" element={<RegisterForm />} />
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/actividad/:childId" element={<ActivityPage />} />
                                <Route path="/que-es-princonser" element={<QueEsPrinconserPage />} />
                                <Route path="/filosofia-princonser" element={<FilosofiaPrinconserPage />} />
                                <Route path="/curriculum-holistico" element={<CurriculumHolisticoPage />} />
                                <Route path="/salud-mental" element={<SaludMentalPage />} />
                                <Route path="/salud-social" element={<SaludSocialPage />} />
                                <Route path="/salud-fisica" element={<SaludFisicaPage />} />
                                <Route path="/salud-financiera" element={<SaludFinancieraPage />} />
                                <Route path="/metodos-ensenanza" element={<MetodosEnsenanzaPage />} />
                                <Route path="/innovaciones" element={<InnovacionesPage />} />
                                <Route path="/gamification" element={<GamificationPage />} />
                                <Route path="/ecosistema-incentivo" element={<EcosistemaIncentivoPage />} />
                                <Route path="/consejo-sabios" element={<ConsejoSabiosPage />} />
                                {/* Rutas de libros */}
                                <Route path="/libros/1" element={<Libro1Page />} />
                                <Route path="/libros/2" element={<Libro2Page />} />
                                <Route path="/libros/3" element={<Libro3Page />} />
                                <Route path="/libros/4" element={<Libro4Page />} />
                                <Route path="/libros/5" element={<Libro5Page />} />
                                <Route path="/libros/6" element={<Libro6Page />} />
                                <Route path="/libros/7" element={<Libro7Page />} />
                                <Route path="/libros/8" element={<Libro8Page />} />
                                <Route path="/explorer" element={<ExplorerForm />} />
                                <Route path="/terminos-condiciones" element={<TerminosCondiciones />} />
                                <Route path="/compensation" element={<CompensationSystem />} />
                                <Route path="/support-materials" element={<SupportMaterials />} />
                                <Route path="/landingpage" element={<LandingPage />} />
                                <Route path="/landingpageEN" element={<LandingPageEN />} />
                                <Route path="/reservar" element={<ReservarPage />} />
                                <Route path="/landingpage2" element={<ReservarPage />} />
                                <Route path="/landingpage2EN" element={<ReservarPageEN />} />
                                <Route path="/reservar/formulario" element={<ReservarFormPage />} />
                                <Route path="/privacidad" element={<PoliticaPrivacidad />} />
                                <Route path="/experimentos" element={<ExperimentosPage />} />
                                <Route path="/investor" element={<InvestorRelations />} />
                                <Route path="/blog-princonser" element={<BlogPrinconser />} />
                                {/* Rutas administrativas protegidas */}
                                <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
                                <Route path="/admin/users" element={<RequireAdmin><AdminUsersList /></RequireAdmin>} />
                                <Route path="/admin/users/:userId" element={<RequireAdmin><AdminUserDetail /></RequireAdmin>} />
                                <Route path="/admin/activities" element={<RequireAdmin><AdminActivitiesList /></RequireAdmin>} />
                                <Route path="/admin/codes" element={<RequireAdmin><CodeGeneratorPanel /></RequireAdmin>} />
                                <Route path="/admin/financial-system" element={<RequireAdmin><FinancialSystemPage /></RequireAdmin>} />
                                <Route path="/admin/tfiat-distribution" element={<RequireAdmin><TfiatDistributionPage /></RequireAdmin>} />
                                <Route path="/admin/treasury" element={<RequireAdmin><Treasury /></RequireAdmin>} />
                                {/* Ruta del sistema de compensación */}
                                <Route path="/compensation" element={<CompensationSystem />} />
        </Routes>
    );
};

const App: React.FC = () => {
    return (
        <LanguageProvider>
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <div className="w-full">
                        <Router>
                            <AppContent />
                            <Chatbot />
                        </Router>
                    </div>
                </LocalizationProvider>
            </ThemeProvider>
        </LanguageProvider>
    );
};

export default App; 